<template>
<div>
    <message-card class="mb-3" state="bad" :message="error_message"></message-card>
    <message-card class="mb-3" state="good" :message="edit_tenant_success_message"></message-card>

    <card size="small" class="mt-3" v-if="facility_stats">
        <template slot="body">
            <div class="flex ai-c jc-sb ta-c">
                <div class="flex-1 flex fd-c jc-c">
                    <strong>Charge Applied</strong>
                    <em :class="{ 'gray-message': facility_stats.charge_applied == 0 }">
                        {{ facility_stats.charge_applied.toLocaleString() }} ({{ facility_stats.charge_applied_percentage }}%)
                    </em>
                </div>

                <div class="flex-1 flex fd-c jc-c">
                    <strong>Opted Out</strong>
                    <em :class="{ 'gray-message': facility_stats.opted_out == 0 }">
                        {{ facility_stats.opted_out.toLocaleString() }} ({{ facility_stats.opted_out_percentage }}%)
                    </em>
                </div>

                <div class="flex-1 flex fd-c jc-c">
                    <strong>Provided Proof</strong>
                    <em :class="{ 'gray-message': facility_stats.provided_proof == 0 }">
                        {{ facility_stats.provided_proof.toLocaleString() }} ({{ facility_stats.provided_proof_percentage }}%)
                    </em>
                </div>

                <div class="flex-1 flex fd-c jc-c">
                    <strong>Selected Coverage</strong>
                    <em :class="{ 'gray-message': facility_stats.selected_coverage == 0 }">
                        {{ facility_stats.selected_coverage.toLocaleString() }} ({{ facility_stats.selected_coverage_percentage }}%)
                    </em>
                </div>

                <div class="flex-1 flex fd-c jc-c">
                    <strong>Potential for Coverage</strong>
                    <em :class="{ 'gray-message': facility_stats.non_responder == 0 }">
                        {{ facility_stats.non_responder.toLocaleString() }} ({{ facility_stats.non_responder_percentage }}%)
                    </em>
                </div>

                <div class="flex-1 flex fd-c jc-c">
                    <strong>Total Active</strong>
                    <em>{{ facility_stats.total.toLocaleString() }}</em>
                </div>
            </div>
        </template>
    </card>

    <card>
        <template slot="header">
            <div class="flex jc-sb">
                <div class="flex ai-c">
                    <i class="material-icons">people</i>
                    <strong class="ml-2">Tenant List</strong>

                    <span class="hint ml-2 mr-2">|</span>

                    <button
                        :disabled="last_row_count === 0 || is_loading_export"
                        class="icon"
                        title="Export"
                        @click="exportData">
                        <i class="material-icons">save_alt</i>
                    </button>

                    <img v-show="is_loading_export" class="ml-3" src="../assets/img/loading-black-32.gif" alt="loading" />
                </div>

                <div class="flex ai-c jc-sb fs-09">
                    <button
                        :disabled="checked.length === 0"
                        type="button"
                        class="small mr-3"
                        @click="onResetTenants">
                        <i class="material-icons">star</i>

                        Reset Tenant<span v-show="checked.length !== 1">s</span>
                    </button>

                    <button :disabled="checked.length === 0" type="button" class="small primary" @click="onBulkSetChargeConfirmed">
                        Bulk Confirm Payment Received
                    </button>

                    <span class="hint ml-2 mr-2">|</span>

                    <button :disabled="checked.length < 1" class="danger small" type="button" @click="onBulkDelete">
                        Delete
                    </button>
                </div>
            </div>
        </template>

        <template slot="body">
            <div v-if="data_info && data_info.incomplete_address > 0" class="danger-text flex ai-c mb-5">
                <span class="mr-2"><i class="material-icons">announcement</i></span>

                <span v-if="data_info.incomplete_address === 1">There is 1 tenant with an incomplete address!</span>
                <span v-else>There are {{ data_info.incomplete_address.toLocaleString() }} tenants with an incomplete address!</span>
            </div>

            <div class="filter-table mt-3">
                <form @submit.prevent="getTenants">
                    <div class="flex ai-c fs-09">
                        <div>
                            <label for="facility-number-filter">Facility Number</label>
                            <input id="facility-number-filter" type="text" v-model="tenant_filter.facility_number" />
                        </div>

                        <div class="flex-a ml-3">
                            <label for="cycle-filter">Cycle</label>
                            <select-input ref="cycle_dropdown"
                                :active-index="cycle_filter.selected_index"
                                :disabled="cycle_filter.options.length < 1"
                                :choices="cycle_filter.options"
                                @change="onChangeCycle">
                            </select-input>
                        </div>
                    </div>

                    <div class="flex ai-c jc-sb mt-3">
                        <div class="flex-0 mr-3 ws-nw">Filter tenants:</div>

                        <select-input
                            :active-index="active_filter_index"
                            :choices="filters"
                            :disabled="is_loading_tenants || filters.length === 0"
                            @change="(index) => active_filter_index = index"></select-input>
                    </div>

                    <div class="flex ai-c jc-sb mt-3">
                        <div class="flex fs-09">
                            <div>
                                <label for="name-filter">Tenant Name</label>
                                <input id="name-filter" type="text" v-model="tenant_filter.name" />
                            </div>

                            <div class="ml-3">
                                <label for="unit-filter">Unit</label>
                                <input id="unit-filter" type="text" v-model="tenant_filter.unit" />
                            </div>

                            <div class="ml-3">
                                <label for="status-filter">Status</label>
                                <select-input
                                    :active-index="status_filter_index"
                                    :choices="status_options"
                                    :fixed="true"
                                    @change="(index) => status_filter_index = index" />
                            </div>

                            <checkbox class="ml-3 pt-3"
                                :checked="filter_tagged"
                                @click="filter_tagged = !filter_tagged">
                                Show Tenants Tagged for Removal
                            </checkbox>
                        </div>

                        <div>
                            <button class="mr-2" type="reset" @click="resetFilters">Reset</button>
                            <button type="submit">Search</button>
                        </div>
                    </div>
                </form>
            </div>

            <div class="flex ai-c jc-sb mb-3 mt-3">
                <div class="flex-33">
                    <button type="button" @click="onToggleAll">
                        <i v-show="checked.length === 0" class="material-icons">check_box_outline_blank</i>
                        <i v-show="checked.length !== 0 && checked.length === tenants.length" class="material-icons">check_box</i>
                        <i v-show="checked.length !== 0 && checked.length !== tenants.length" class="material-icons">indeterminate_check_box</i>
                    </button>
                </div>

                <div class="flex-33 ta-c">
                    <vue-table-pagination
                        ref="tenants_table_pagination"
                        @vuetable-pagination:change-page="onChangePage">
                    </vue-table-pagination>
                </div>

                <div class="flex-33 flex ai-c jc-fe ta-r">
                    <vue-table-pagination-info ref="tenants_table_info"
                        info-template="{total} total tenants"
                        no-data-template="" />

                    <button class="ml-4" title="Refresh" type="button" :disabled="is_loading_tenants" @click="getTenants">
                        <i class="material-icons">refresh</i>
                    </button>
                </div>
            </div>

            <vue-table ref="tenants_table"
                :class="['table-loads', { 'loading': is_loading_tenants }]"
                api-url="/api/tenants"
                :load-on-start="false"
                :per-page="100"
                table-height="475px"
                :append-params="api_params"
                data-path="data.data"
                pagination-path="data.pagination"
                :http-options="http_options"
                :fields="columns"
                :data-manager="manageTableFunc"
                no-data-template="<span>No tenants to show.</span>"
                @vuetable:pagination-data="onPaginationData"
                @vuetable:loading="is_loading_tenants = true"
                @vuetable:loaded="is_loading_tenants = false"
                @vuetable:load-error="onTableLoadError"
                @vuetable:load-success="onTableLoadSuccess"
                @vuetable:row-clicked="onRowClicked">
                <template slot="checkbox" slot-scope="props">
                    <checkbox
                        :alone="true"
                        :center="true"
                        :checked="checked.indexOf(props.rowData.id) !== -1"
                        @click="toggleCheckbox(props.rowData.id)"></checkbox>
                </template>

                <template slot="name" slot-scope="props">
                    <div class="flex jc-sb">
                        <span
                            v-if="props.rowData.last_name !== null"
                            class="row-name"
                            :title="`${props.rowData.last_name}, ${props.rowData.first_name}`">
                            {{ props.rowData.last_name }}, {{ props.rowData.first_name }}
                        </span>
                        <span
                            v-else
                            class="row-name"
                            :title="props.rowData.first_name">
                            {{ props.rowData.first_name }}
                        </span>

                        <span
                            v-show="props.rowData.tagged_for_removal"
                            class="as-c"
                            title="Flagged for deletion">
                            <i class="danger-text material-icons">flag</i>
                        </span>
                    </div>
                </template>

                <template slot="status" slot-scope="props">
                    <div v-if="props.rowData.status === null" class="gray-message">No response</div>

                    <div v-else class="flex ai-c jc-sb">
                        <div>
                            <span :class="[props.rowData.status.toLowerCase().replace(' ', '-')]">
                                {{ props.rowData.status }}
                            </span>

                            <strong v-if="props.rowData.tenant_made_selection" class="badge cu-h ml-1" title="Tenant made selection">
                                T
                            </strong>
                        </div>

                        <span>
                            <button
                                class="small"
                                type="button"
                                @click.stop="openEditStatusModal(props.rowData)">
                                <i class="material-icons">edit</i>
                            </button>
                        </span>
                    </div>
                </template>

                <template slot="setup_confirmed" slot-scope="props">
                    <span class="success-text cu-h" v-if="props.rowData.charge_applied_date !== null" title="Manager confirmed insurance setup">
                        <i class="material-icons">check_circle</i>
                    </span>

                    <span class="badge secondary cu-h" v-if="props.rowData.setup_insurance_date !== null" title="Tenant was setup via API">
                        API
                    </span>
                </template>

                <template slot="api_confirmed" slot-scope="props">
                    <span class="success-text" v-if="props.rowData.charge_confirmed_date !== null">
                        <i class="material-icons">check_circle</i>
                    </span>
                </template>

                <template slot="payment_confirmed" slot-scope="props">
                    <span class="success-text" v-if="props.rowData.payment_confirmed_date !== null">
                        <i class="material-icons">check_circle</i>
                    </span>
                </template>

                <template slot="units" slot-scope="props">
                    {{ props.rowData.units.map(unit => unit.identifier).join(", ") }}
                </template>

                <template slot="welcome_letter_sent_date" slot-scope="props">
                    <span v-if="props.rowData.welcome_letter_sent_date !== null && props.rowData.welcome_certificate_emailed_date !== null">
                        <span class="material-icons cu-h mr-1" :title="`Certificate also emailed: ${props.rowData.welcome_certificate_emailed_date}`">
                            all_inbox
                        </span>

                        <span v-html="formatDate(props.rowData.welcome_letter_sent_date)"></span>
                    </span>

                    <span v-else-if="props.rowData.welcome_letter_sent_date !== null">
                        <span class="material-icons cu-h mr-1" title="Mailed">
                            mail
                        </span>

                        <span v-html="formatDate(props.rowData.welcome_letter_sent_date)"></span>
                    </span>

                    <span v-else-if="props.rowData.welcome_certificate_emailed_date !== null">
                        <span class="material-icons cu-h mr-1" title="Certificate Emailed">
                            inbox
                        </span>

                        <span v-html="formatDate(props.rowData.welcome_certificate_emailed_date)"></span>
                    </span>

                    <span v-else class="null">NULL</span>
                </template>
            </vue-table>
        </template>
    </card>

    <modal v-if="edit_modal_open" @close="onEditModalClose" size="large">
        <template slot="header">
            Edit Tenant (Unit {{ edit_tenant.unit }})
        </template>

        <template slot="content">
            <div class="flex">
                <div class="flex-50 mr-2">
                    <label for="first-name">First Name</label>
                    <input id="first-name" type="text" v-model="edit_tenant.first_name" />
                </div>

                <div class="flex-50 ml-2">
                    <label for="last-name">Last Name</label>
                    <input id="last-name" type="text" v-model="edit_tenant.last_name" />
                </div>
            </div>

            <div class="flex mt-3">
                <div class="flex-a">
                    <label for="email">Email</label>
                    <input id="email" type="text" v-model="edit_tenant.email" />
                </div>
            </div>

            <div class="flex mt-3">
                <div class="flex-a">
                    <label for="address">Address</label>
                    <input id="address" type="text" v-model="edit_tenant.address" />
                </div>
            </div>

            <div class="flex mt-3">
                <div class="flex-33 mr-2">
                    <label for="city">City</label>
                    <input id="city" type="text" v-model="edit_tenant.city" />
                </div>

                <div class="flex-33 nl-2 mr-2">
                    <label for="state">State</label>
                    <select-input
                        :active-index="active_state_index"
                        :choices="state_options"
                        :fixed="true"
                        @change="onChangeState"></select-input>
                </div>

                <div class="flex-33 ml-2">
                    <label for="zip-code">Zip Code</label>
                    <input id="zip-code" type="text" v-model="edit_tenant.zip_code" />
                </div>
            </div>

            <div class="flex mt-3">
                <div class="flex-50 mr-2">
                    <label for="move-in-date">Move-In Date</label>
                    <datepicker :typeable="true" v-model="edit_tenant.move_in_date" :format="'yyyy-MM-dd'"></datepicker>
                </div>

                <div class="flex-50 ml-2">
                    <label for="billing-date">Billing Date</label>
                    <datepicker :typeable="true" v-model="edit_tenant.billing_date" :format="'yyyy-MM-dd'"></datepicker>
                </div>
            </div>

            <div class="mt-3">
                <checkbox
                    :checked="!!edit_tenant.charge_confirmed_date"
                    :disabled="is_loading_tenant_edit"
                    @click="edit_tenant.charge_confirmed_date = !!!edit_tenant.charge_confirmed_date">
                    Confirm payment received
                </checkbox>
            </div>

            <div v-if="edit_tenant.tagged_for_removal" class="mt-3">
                <hr />
                <div class="mb-2"><i class="danger-text material-icons">flag</i> Tenant is flagged for removal. Unflag them?</div>
                <checkbox
                    :checked="edit_tenant.untag"
                    @click="edit_tenant.untag = !edit_tenant.untag">
                    Yes
                </checkbox>
            </div>

            <div class="flex jc-sb mt-3">
                <div class="flex-50 mr-2">
                    <label for="note">Tenant Notes (viewable by Manager)</label>
                    <textarea id="note" v-model="edit_tenant.note" rows="4" />

                    <div v-if="edit_tenant.note_created !== null" class="mt-2">
                        <em>Note created: {{ dayjs(edit_tenant.note_created).format("M/DD/YYYY") }}</em>
                    </div>
                </div>

                <div class="flex-50 ml-2">
                    <label for="monitor-note">Monitor Notes</label>
                    <textarea id="monitor" v-model="edit_tenant.monitor_note" rows="4" />
                </div>
            </div>

            <div class="flex ai-c jc-sb mt-5">
                <div>
                    <div v-show="edit_tenant_error_message">
                        <span class="badger danger">
                            <i class="material-icons">priority_high</i>
                        </span>
                        <span class="d-ib ml-2">{{ edit_tenant_error_message }}</span>
                    </div>
                </div>

                <div class="flex">
                    <div v-show="is_loading_tenant_edit" class="flex ai-c mr-2">
                        <img src="../assets/img/loading-black-32.gif" alt="loading" />
                    </div>

                    <button :disabled="is_loading_tenant_edit" type="button" @click="onSaveTenant">Save</button>
                </div>
            </div>
        </template>
    </modal>

    <modal v-show="confirm_delete_modal_open" @close="confirm_delete_modal_open = false">
        <template slot="header">
            Are you sure?
        </template>

        <template slot="content">
            {{ checked.length }} tenants will be permanently deleted. Are you sure you want to do this?
        </template>

        <template slot="footer">
            <div class="flex jc-fe">
                <div v-show="is_loading_bulk_delete" class="flex ai-c mr-3">
                    <img src="../assets/img/loading-black-32.gif" alt="loading" />
                </div>

                <button :disabled="is_loading_bulk_delete" type="button" @click="confirm_delete_modal_open = false">No</button>
                <button :disabled="is_loading_bulk_delete" class="ml-3" type="button" @click="onConfirmBulkDelete">Yes</button>
            </div>
        </template>
    </modal>

    <edit-tenant-status-modal
        v-if="edit_status_modal_open"
        :tenant="edit_status_tenant"
        @close="onCloseEditTenantStatusModal"
        @save="false"></edit-tenant-status-modal>

    <modal v-show="bulk_edit_modal_open" @close="bulk_edit_modal_open = false">
        <template slot="header">
            Bulk Edit
        </template>

        <template slot="content">
            <div v-show="is_loading_bulk_edit" class="flex jc-fe mb-3">
                <img src="../assets/img/loading-black-32.gif" alt="loading" />
            </div>

            <label for="first-name">First Name</label>
            <div class="flex">
                <div class="flex-a mr-2">
                    <input id="first-name" class="full" type="text" v-model="bulk_edit.first_name" />
                </div>

                <div class="flex-0 ml-2">
                    <button :disabled="is_loading_bulk_edit" type="button" @click="onBulkEditSave('first_name')">Update</button>
                </div>
            </div>

            <label class="mt-3" for="last-name">Last Name</label>
            <div class="flex">
                <div class="flex-a mr-2">
                    <input id="last-name" class="full" type="text" v-model="bulk_edit.last_name" />
                </div>

                <div class="flex-0 ml-2">
                    <button :disabled="is_loading_bulk_edit" type="button" @click="onBulkEditSave('last_name')">Update</button>
                </div>
            </div>

            <label class="mt-3" for="move-in-date">Move-In Date</label>
            <div class="flex">
                <div class="flex-a mr-2">
                    <datepicker v-model="bulk_edit.move_in_date" :format="'yyyy-MM-dd'"></datepicker>
                </div>

                <div class="flex-0 ml-2">
                    <button :disabled="is_loading_bulk_edit" type="button" @click="onBulkEditSave('move_in_date')">Update</button>
                </div>
            </div>

            <label class="mt-3" for="country">Country</label>
            <div class="flex">
                <div class="flex-a mr-2">
                    <input id="country" class="full" type="text" v-model="bulk_edit.country" />
                </div>

                <div class="flex-0 ml-2">
                    <button :disabled="is_loading_bulk_edit" type="button" @click="onBulkEditSave('country')">Update</button>
                </div>
            </div>
        </template>
    </modal>
</div>
</template>

<script>
import dayjs from "dayjs"
import http from "@/http"
import store from "@/store"
import Vue from "vue"
import { debounce } from "lodash"
import { tenantListColumns, formatDate } from "../utilities/columns"
import { parseQueryParameters } from "@/utilities/path"
import { objectToParams } from "@/utilities/form-data"
import { statesByAbbr } from "@/utilities/states"

export default {
    name: "tenant-list-page",

    data () {
        return {
            active_filter_index: 0,
            active_state_index: -1,
            bulk_edit: {},
            bulk_edit_modal_open: false,
            checked: [],
            columns: tenantListColumns,
            confirm_delete_modal_open: false,
            cycle_filter: {
                selected_index: -1,
                options: []
            },
            data_info: null,
            dayjs,
            edit_modal_open: false,
            edit_status_tenant: {},
            edit_status_modal_open: false,
            edit_tenant: {},
            edit_tenant_error_message: "",
            edit_tenant_success_message: "",
            error_message: "",
            facilities: [],
            facility: null,
            facility_query: "",
            facility_number_filter: "",
            facility_stats: null,
            filter_tagged: false,
            filters: [],
            formatDate: formatDate,
            http_options: {
                baseURL: this.$http.defaults.baseURL,
                headers: {
                    "X-Authorization": store.state.token.token
                },
                transformResponse: [(raw) => {
                    try {
                        return JSON.parse(raw)
                    } catch (error) {
                        this.load_tenants_error = "Unknown error loading tenants."

                        return raw
                    }
                }]
            },
            is_loading_bulk_delete: false,
            is_loading_bulk_edit: false,
            is_loading_export: false,
            is_loading_facilities: false,
            is_loading_search: false,
            is_loading_stats: false,
            is_loading_tenant_edit: false,
            is_loading_tenants: false,
            last_row_count: 0,
            manageTableFunc: this.manageTable.bind(this),
            show_filters: false,
            sort_field: "",
            state_options: statesByAbbr,
            status_filter_index: 0,
            status_options: [
                { key: "all", label: "All Tenants", badge: null },
                { key: "move_out", label: "Moved out", badge: null },
                { key: "bad_address", label: "Bad Address", badge: null },
                { key: "any_coverage", label: "Selected Coverage", badge: null },
                { key: "provide_proof", label: "Provided Proof", badge: null },
                { key: "opt_out", label: "Opted Out", badge: null },
                { key: "non_responder", label: "No Response", badge: null }
            ],
            tenant_filter: { facility_number: null, name: null, unit: null },
            tenants: []
        }
    },

    computed: {
        api_params () {
            const params = {}

            const filter = this.filters[this.active_filter_index]
            if (filter) {
                params.filter = filter.key
            }

            if (this.selected_cycle && this.selected_cycle.key !== "none") {
                params.cycle_id = this.selected_cycle.key
            }

            for (const key in this.tenant_filter) {
                const value = this.tenant_filter[key]

                if (value && value.length > 1) {
                    params[key] = value
                }
            }

            if (this.filter_tagged) {
                params.tagged_for_removal = true
            }

            const statusFilter = this.status_options[this.status_filter_index]
            params.status = statusFilter.key

            return params
        },

        facility_options () {
            return this.facilities.map(facility => {
                return {
                    key: facility.id,
                    label: `(${facility.facility_number}) ${facility.address}, ${facility.city}, ${facility.state} ${facility.zip_code}`
                }
            })
        },

        selected_cycle () {
            if (this.cycle_filter.selected_index === -1) {
                return null
            }

            return this.cycle_filter.options[this.cycle_filter.selected_index]
        },

        selected_facility () {
            let result = this.facilities.filter(facility => facility.id === this.facility)

            if (result && result.length) {
                return result[0]
            } else {
                return []
            }
        }
    },

    watch: {
        active_filter_index () {
            this.getTenants()
        },

        sort_field: {
            deep: true,
            handler (newValue) {
                if (newValue.field && this.tenants !== undefined) {
                    // the sort happens in place, so the table is automatically updated
                    this.tenants.sort((a, b) => {
                        if (a[newValue.field] === b[newValue.field]) {
                            return 0
                        }

                        if (newValue.direction === "asc") {
                            if (a[newValue.field] > b[newValue.field]) {
                                return 1
                            } else {
                                return -1
                            }
                        }

                        if (a[newValue.field] < b[newValue.field]) {
                            return 1
                        } else {
                            return -1
                        }
                    })
                }
            }
        },

        filter_tagged (newValue) {
            this.getTenants()
        },

        status_filter_index () {
            this.getTenants()
        },

        "tenant_filter.facility_number": {
            handler: debounce(async function (newValue) {
                await this.getCycles(newValue)
            }, 500)
        },

        "cycle_filter.selected_index" () {
            this.getTenants()
        }
    },

    mounted () {
        const params = parseQueryParameters()

        if (params.facility_number) {
            this.tenant_filter.facility_number = params.facility_number
        }

        this.getTenants()
    },

    methods: {
        async getCycles (facilityNumber) {
            if (!facilityNumber) {
                return
            }

            this.cycle_filter.options = []

            try {
                const params = parseQueryParameters()
                const query = encodeURIComponent(facilityNumber)

                const { data: response } = await http(`/api/cycles/search?facility_number=${query}`)

                let options = [
                    { key: "none", label: "All", badge: null }
                ]

                options = options.concat(response.map((cycle) => {
                    return {
                        key: cycle.id,
                        label: cycle.label,
                        badge: cycle.tenant_count
                    }
                }))

                this.cycle_filter.options = options

                if (params.cycle_id) {
                    let matchedCycleId = null

                    for (const index in this.cycle_filter.options) {
                        const cycle = this.cycle_filter.options[index]

                        if (cycle.key === parseInt(params.cycle_id)) {
                            matchedCycleId = index
                        }
                    }

                    if (matchedCycleId !== null) {
                        this.cycle_filter.selected_index = matchedCycleId
                    } else {
                        this.cycle_filter.selected_index = 0
                    }
                } else {
                    this.cycle_filter.selected_index = 0
                }
            } catch (ex) {
                if (ex.message) {
                    this.$eventManager.emit("add-toast", { message: ex.message, type: "error" })
                } else {
                    this.$eventManager.emit("add-toast", { message: ex, type: "error" })
                }
            }
        },

        async getTenants () {
            Vue.nextTick(() => {
                this.$refs.tenants_table.reload()
            })
        },

        resetFilters () {
            for (const key in this.tenant_filter) {
                this.tenant_filter[key] = ""
            }

            this.status_filter_index = 0

            this.cycle_filter.selected_index = -1
            this.cycle_filter.options = []

            this.getTenants()
        },

        async exportData () {
            this.is_loading_export = true

            try {
                const {
                    data: {
                        filename: fileKey
                    }
                } = await http("/api/exports/monitor-tenants", { params: this.api_params })

                const activeFilter = this.filters[this.active_filter_index]

                let url = `${http.defaults.baseURL}/api/exports/download`
                url += `?file_key=${fileKey}&filename=alp_tenants_${activeFilter.key}`

                window.open(url, "_blank")
            } catch (ex) {
                let message

                if (ex === undefined) {
                    message = "Unexpected error occurred"
                } else {
                    message = ex.message || ex

                    console.dir(ex)
                }

                this.$eventManager.emit("add-toast", { message, type: "error" })
            } finally {
                this.is_loading_export = false
            }
        },

        /**
         * Set the sort field when the user requests sorting.
         *
         * @param object data Row data
         *
         * @return null
         */
        manageTable (data) {
            if (data.length) {
                this.sort_field = {
                    field: data[0].sortField,
                    direction: data[0].direction
                }
            } else {
                this.sort_field = {}
            }
        },

        /**
         * Open the confirm bulk delete modal.
         *
         * @return null
         */
        onBulkDelete () {
            if (this.checked.length) {
                this.confirm_delete_modal_open = true
            }
        },

        /**
         * Open the bulk edit modal.
         *
         * @return null
         */
        onBulkEdit () {
            if (this.checked.length) {
                this.bulk_edit_modal_open = true
            }
        },

        /**
         * Send the bulk edit request to the server.
         *
         * @return null
         */
        async onBulkEditSave (key) {
            const params = new URLSearchParams()

            this.is_loading_bulk_edit = true

            params.append("field", key)
            params.append("ids", this.checked.join(","))

            if (this.bulk_edit[key] === undefined) {
                params.append("value", null)
            } else {
                params.append("value", this.bulk_edit[key])
            }

            try {
                await http.post("/api/tenants/bulk", params)

                this.bulk_edit_modal_open = false
                this.getTenants()
            } catch (ex) {
                this.error_message = ex
            } finally {
                this.is_loading_bulk_edit = false
            }
        },

        onChangeCycle (index) {
            this.cycle_filter.selected_index = index
        },

        /**
         * Update the state field when a state is picked from the list.
         *
         * @param int index Chose state option index
         */
        onChangeState (index) {
            this.active_state_index = index

            this.edit_tenant.state = this.state_options[index].key
        },

        /**
         * Update the tenants list when the edit tenant status modal is closed.
         */
        onCloseEditTenantStatusModal () {
            this.edit_status_modal_open = false

            this.getTenants()
        },

        /**
         * Send the bulk delete request to the server.
         *
         * @return null
         */
        async onConfirmBulkDelete () {
            const params = {
                ids: this.checked.join(",")
            }

            this.error_message = ""
            this.is_loading_bulk_delete = true

            try {
                await http.delete("/api/tenants/bulk", { params: params })

                this.checked = []
                this.getTenants()
            } catch (ex) {
                this.error_message = ex
            } finally {
                this.is_loading_bulk_delete = false
                this.confirm_delete_modal_open = false
            }
        },

        /**
         * Open the edit tenant modal.
         *
         * @param object data  Row data object
         * @param Event  ev    Event object
         *
         * @return null
         */
        onRowClicked (data, ev) {
            let tenantData = Object.assign({}, data)

            // let columns prevent clicking from happening
            if (ev.composedPath()[0].classList.contains("no-click")) {
                return
            }

            if (tenantData.billing_date !== null && !(tenantData.billing_date instanceof Date)) {
                const parts = tenantData.billing_date.split("-")

                if (parts.length === 3) {
                    // month is 0-index
                    parts[1] = parts[1] - 1

                    tenantData.billing_date = new Date(...parts)
                }
            }

            // set the state option for the selected tenant
            this.state_options.forEach((option, index) => {
                if (option.key === tenantData.state) {
                    this.active_state_index = index
                }
            })

            this.edit_tenant = Object.assign({ untag: false }, tenantData)
            this.edit_modal_open = true
        },

        openEditStatusModal (tenantData) {
            this.edit_status_tenant = Object.assign({}, tenantData)
            this.edit_status_modal_open = true
        },

        /**
         * Close the edit tenant modal and unload the edit tenant data.
         *
         * @return null
         */
        onEditModalClose () {
            this.edit_tenant = {}
            this.active_state_index = -1
            this.edit_modal_open = false
        },

        /**
         * Change the pagination page when requested.
         *
         * @param object page Page
         *
         * @return null
         */
        onChangePage (page) {
            this.$refs.tenants_table.changePage(page)
        },

        /**
         * Update the pagination component when data is received.
         *
         * @param object paginationData Pagination data
         *
         * @return null
         */
        onPaginationData (paginationData) {
            this.$refs.tenants_table_pagination.setPaginationData(paginationData)
            this.$refs.tenants_table_info.setPaginationData(paginationData)
        },

        async onResetTenants () {
            const count = this.checked.length

            if (count > 0) {
                const confirmed = confirm(`Are you sure you want to reset ${count} tenants?`)

                if (confirmed) {
                    this.error_message = ""

                    const params = new URLSearchParams()
                    params.append("ids", this.checked.join(","))

                    try {
                        await http.post("/api/tenants/reset", params)

                        this.$refs.tenants_table.reload()
                    } catch (ex) {
                        this.error_message = ex
                    }
                }
            }
        },

        /**
         * Send the tenant updates to the server.
         *
         * @return null
         */
        async onSaveTenant () {
            const params = new URLSearchParams()

            this.is_loading_tenant_edit = true
            this.edit_tenant_error_message = ""
            this.edit_tenant_success_message = ""

            Object.keys(this.edit_tenant).forEach(key => {
                const value = this.edit_tenant[key]

                if (this.edit_tenant[key] instanceof Date) {
                    let str
                    let month = value.getMonth() + 1
                    let day = value.getDate()
                    let hours = value.getHours()
                    let minutes = value.getMinutes()
                    let seconds = value.getSeconds()

                    if (month < 10) {
                        month = `0${month}`
                    }

                    if (day < 10) {
                        day = `0${day}`
                    }

                    if (hours < 10) {
                        hours = `0${hours}`
                    }

                    if (minutes < 10) {
                        minutes = `0${minutes}`
                    }

                    if (seconds < 10) {
                        seconds = `0${seconds}`
                    }

                    str = `${value.getFullYear()}-${month}-${day}`
                    str += ` ${hours}:${minutes}:${seconds}`

                    params.append(key, str)
                } else if (key === "untag") {
                    if (value === true) {
                        params.append("untag", true)
                    }
                } else if (key === "units") {
                    params.append("units", this.edit_tenant[key].map(unit => unit.identifier).join(","))
                } else {
                    params.append(key, this.edit_tenant[key])
                }
            })

            try {
                await http.post(`/api/tenants/${this.edit_tenant.id}`, params)

                this.edit_modal_open = false

                this.$eventManager.emit("add-toast", { message: `Tenant record was successfully updated for ${this.edit_tenant.first_name} ${this.edit_tenant.last_name}` })

                this.getTenants()

                this.edit_tenant = {}
                this.active_state_index = -1
            } catch (ex) {
                this.edit_tenant_error_message = ex
            } finally {
                this.is_loading_tenant_edit = false
            }
        },

        onTableLoadError ({ response: { data } }) {
            console.warn(data)

            this.$eventManager.emit("add-toast", { message: "Error loading tenants", type: "error" })
        },

        /**
         * Get the loaded record count when the table successfully loads data.
         *
         * @param {object} data Server response data object
         */
        onTableLoadSuccess ({ data: { data: response } }) {
            this.last_row_count = response.data.length
            this.filters = response.filters
            this.data_info = response.info
        },

        async onBulkSetChargeConfirmed () {
            const message = `This will confirm the charge has been added for ${this.checked.length} tenants. Continue?`
            const confirmed = confirm(message)

            if (confirmed) {
                this.is_loading_tenants = true

                try {
                    const params = objectToParams({
                        ids: this.checked.join(","),
                        field: "charge_confirmed",
                        value: true
                    })

                    await http.post("/api/tenants/bulk", params)

                    this.$eventManager.emit("add-toast", { message: `${this.checked.length} tenants successfully updated!` })
                    this.checked = []

                    this.$refs.tenants_table.reload()
                } catch (ex) {
                    this.is_loading_tenants = false
                }
            }
        },

        /**
         * Toggle selecting all tenants.
         *
         * @return null
         */
        onToggleAll () {
            const tenants = this.$refs.tenants_table.tableData

            if (tenants.length === this.checked.length) {
                this.checked = []
            } else {
                this.checked = tenants.map(tenant => tenant.id)
            }
        },

        /**
         * Toggle the state of a facility's checkbox.
         *
         * @param int id Facility ID
         *
         * @return null
         */
        toggleCheckbox (id) {
            if (this.checked.indexOf(id) === -1) {
                this.checked.push(id)
            } else {
                this.checked = this.checked.filter(checkId => checkId !== id)
            }
        }
    }
}
</script>

<style>
.facility-info-table {
    border-collapse: collapse;
    table-layout: fixed;
}

.facility-info-table td {
    vertical-align: top;
}

.facility-info-table td:first-child {
    font-weight: 700;
    padding-right: 10px;
    text-align: right;
    width: 130px;
}
</style>

<style scoped>
.row-name {
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>



// WEBPACK FOOTER //
// src/pages/TenantListPage.vue